import './styles.scss';
import React, { useState, useEffect } from "react";
import { Box, Button, Checkbox, Icon, Modal, SpaceBetween } from "@amzn/awsui-components-react";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from 'uuid';
// TODO: Consider moving this entire file to /components folder since it should be generic and reusable

type BucketId = string;
type RowId = string;

export type Bucket = {
  id: BucketId;
  title: string;
  rowIds: RowId[];
  // When true, the group is conditional on the execute page: it renders as a parent
  // checkbox and its parameters stay hidden until the end user checks it. Undefined /
  // false = always visible (legacy behavior).
  is_mandatory?: boolean;
};

export type Buckets = Record<BucketId, Bucket>;

// Generic type - can be any data object with an id
export type Row<T = any> = T & { id: RowId };

const findBucketId = (
  buckets: Buckets,
  rowId: string
): BucketId | null => {
  for (const bucket of Object.values(buckets)) {
    if (bucket.rowIds.includes(rowId)) return bucket.id;
  }
  return null;
}

const BucketDroppable = ({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div ref={setNodeRef} className={`bucket-droppable ${isOver ? 'is-over' : ''}`}>
      {children}
    </div>
  );
}

const SortableBucket = <T,>({
  bucket,
  rows,
  onTitleChange,
  onFlagChange,
  onDeleteBucket,
  onEditRow,
  onDeleteRow,
  renderItem
}: {
  bucket: Bucket;
  rows: Record<RowId, Row<T>>;
  onTitleChange: (bucketId: string, newTitle: string) => void;
  onFlagChange: (bucketId: string, isMandatory: boolean) => void;
  onDeleteBucket: (bucketId: string) => void;
  onEditRow: (rowId: string, updatedRow: Row<T>) => void;
  onDeleteRow: (rowId: string) => void;
  renderItem: (row: Row<T>, handlers: { onEdit: (updatedRow: Row<T>) => void; onDelete: () => void; canDelete: boolean }) => { content: React.ReactNode; canDelete: boolean };
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: bucket.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div ref={setNodeRef} style={style}>
      <BucketColumn
        bucket={bucket}
        rows={rows}
        onTitleChange={onTitleChange}
        onFlagChange={onFlagChange}
        onDeleteBucket={onDeleteBucket}
        onEditRow={onEditRow}
        onDeleteRow={onDeleteRow}
        dragHandleProps={{ ...attributes, ...listeners }}
        renderItem={renderItem}
      />
    </div>
  );
}

const SortableRowItem = <T,>({
  rowId,
  rows,
  onEditRow,
  onDeleteRow,
  renderItem
}: {
  rowId: string;
  rows: Record<RowId, Row<T>>;
  onEditRow?: (rowId: string, updatedRow: Row<T>) => void;
  onDeleteRow?: (rowId: string) => void;
  renderItem: (row: Row<T>, handlers: { onEdit: (updatedRow: Row<T>) => void; onDelete: () => void; canDelete: boolean }) => { content: React.ReactNode; canDelete: boolean };
}) => {
  const row = rows[rowId];
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: row.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  
  const result = renderItem(row, { 
    onEdit: (updatedRow: Row<T>) => onEditRow?.(row.id, updatedRow), 
    onDelete: () => onDeleteRow?.(row.id), 
    canDelete: true 
  });
  
  return (
    <div ref={setNodeRef} style={style} className="row-item">
      <button {...attributes} {...listeners} className="drag-handle"><Icon name="drag-indicator" /></button>
      {result.content}
      {result.canDelete && <Button onClick={() => onDeleteRow?.(row.id)} iconName='remove' variant='icon' />}
    </div>
  );
};

const BucketColumn = <T,>({
  bucket,
  rows,
  onTitleChange,
  onFlagChange,
  onDeleteBucket,
  onEditRow,
  onDeleteRow,
  isEditable = true,
  dragHandleProps,
  renderItem
}: {
  bucket: Bucket;
  rows: Record<RowId, Row<T>>;
  onTitleChange: (bucketId: string, newTitle: string) => void;
  onFlagChange?: (bucketId: string, isMandatory: boolean) => void;
  onDeleteBucket?: (bucketId: string) => void;
  onEditRow?: (rowId: string, updatedRow: Row<T>) => void;
  onDeleteRow?: (rowId: string) => void;
  isEditable?: boolean;
  dragHandleProps?: Record<string, any>;
  renderItem: (row: Row<T>, handlers: { onEdit: (updatedRow: Row<T>) => void; onDelete: () => void; canDelete: boolean }) => { content: React.ReactNode; canDelete: boolean };
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(bucket.title);

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue.trim()) {
      onTitleChange(bucket.id, editValue.trim());
    } else {
      setEditValue(bucket.title);
    }
  }

  return (
    <div className={`bucket-column ${isEditable ? '' : 'ungrouped'}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {dragHandleProps && (
          <button {...dragHandleProps} className="bucket-drag-handle">
            <Icon name="drag-indicator" />
          </button>
        )}
        {isEditable && isEditing ? (
          <input
            id={`bucket-title-${bucket.id}`}
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleBlur()}
            className="bucket-title--input"
          />
        ) : (
          <h3
            onClick={() => isEditable && setIsEditing(true)}
            style={{ cursor: isEditable ? 'pointer' : 'default' }}
          >
            {bucket.title}{isEditable ? <div className="bucket-title--edit-wrapper"><Icon name="edit"/></div> : ''}
          </h3>
        )}
        {isEditable && onFlagChange && (
          <Checkbox
            checked={!!bucket.is_mandatory}
            onChange={({ detail }) => onFlagChange(bucket.id, detail.checked)}
          >
            Show on selection
          </Checkbox>
        )}
        </div>
        {onDeleteBucket && (
          <Button onClick={() => onDeleteBucket(bucket.id)} variant='normal' iconName='remove'>Delete Group</Button>
        )}
      </div>

      <BucketDroppable id={bucket.id}>
        <SortableContext
          items={bucket.rowIds}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {bucket.rowIds.length === 0 ? (
              <div className="empty-bucket">
                {isEditable ? `Drop parameter rows here` : `New parameter rows will be added here`}
              </div>
            ) : (
              bucket.rowIds
                .filter((rowId) => rows[rowId])
                .map((rowId) => (
                  <SortableRowItem
                    key={rowId}
                    rowId={rowId}
                    rows={rows}
                    onEditRow={onEditRow}
                    onDeleteRow={onDeleteRow}
                    renderItem={renderItem}
                  />
                ))
            )}
          </div>
        </SortableContext>
      </BucketDroppable>
    </div>
  );
}

interface DraggableGroupedListProps<T,> {
  initialRows: Record<RowId, Row<T>>;
  initialBuckets?: Buckets; // Optional - component creates ungrouped automatically
  initialBucketOrder?: string[]; // Optional - defaults to empty array
  onDataChange?: (rows: Record<RowId, Row<T>>, buckets: Buckets, bucketOrder: string[]) => void;
  onAddRow: () => void;
  onDeleteRow: (rowId: string) => void;
  renderItem: (row: Row<T>, handlers: { onEdit: (updatedRow: Row<T>) => void; onDelete: () => void; canDelete: boolean }) => { content: React.ReactNode; canDelete: boolean };
}

const DraggableGroupedList = <T,>({
  initialRows,
  initialBuckets = {},
  initialBucketOrder = [],
  onDataChange,
  onAddRow,
  onDeleteRow,
  renderItem
}: DraggableGroupedListProps<T>) => {
  // Ensure ungrouped bucket always exists
  const ensureUngroupedBucket = (buckets: Buckets, rows: Record<RowId, Row<T>>): Buckets => {
    const ungroupedRowIds = Object.values(rows)
      .filter((row: any) => !row.group_id || row.group_id === 'ungrouped')
      .map(row => row.id);
    
    return {
      ungrouped: { id: 'ungrouped', title: 'Ungrouped', rowIds: ungroupedRowIds },
      ...buckets
    } as Buckets;
  };

  const [rows, setRows] = useState(initialRows);
  const [buckets, setBuckets] = useState(() => ensureUngroupedBucket(initialBuckets, initialRows));
  const [bucketOrder, setBucketOrder] = useState(initialBucketOrder);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showDeleteBucketModal, setShowDeleteBucketModal] = useState<boolean>(false);
  const [bucketToDelete, setBucketToDelete] = useState<string | null>(null);

  useEffect(() => {
    const newRowIds = Object.keys(initialRows);
    const currentRowIds = Object.keys(rows);
    const addedIds = newRowIds.filter(id => !currentRowIds.includes(id));
    
    if (addedIds.length > 0) {
      const newRows = { ...rows };
      addedIds.forEach(id => {
        newRows[id] = initialRows[id];
      });
      setRows(newRows);
      
      setBuckets(prev => ({
        ...prev,
        ungrouped: {
          ...prev.ungrouped,
          rowIds: [...prev.ungrouped.rowIds, ...addedIds]
        }
      }));
    }
  }, [initialRows]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const bucketIds = bucketOrder;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const isActiveBucket = bucketIds.includes(activeId);
    const isOverBucket = bucketIds.includes(overId);

    if (isActiveBucket && isOverBucket) return;

    const sourceBucketId = findBucketId(buckets, activeId);
    const overBucketId =
      (bucketIds.includes(overId)
        ? overId
        : findBucketId(buckets, overId)) ?? null;

    if (!sourceBucketId || !overBucketId) return;
    if (sourceBucketId === overBucketId) return;

    const source = buckets[sourceBucketId];
    const destination = buckets[overBucketId];

    const sourceIndex = source.rowIds.indexOf(activeId);
    if (sourceIndex === -1) return;

    const nextSourceIds = source.rowIds.filter((id) => id !== activeId);

    const overIndex = destination.rowIds.indexOf(overId);
    const insertIndex =
      overIndex >= 0 ? overIndex : destination.rowIds.length;

    const nextDestIds = [
      ...destination.rowIds.slice(0, insertIndex),
      activeId,
      ...destination.rowIds.slice(insertIndex),
    ];
    
    const updatedRow = { 
      ...rows[activeId], 
      display_index: insertIndex
    };
    const updatedRows = { ...rows, [activeId]: updatedRow };

    const newBuckets = {
      ...buckets,
      [sourceBucketId]: { ...source, rowIds: nextSourceIds },
      [overBucketId]: { ...destination, rowIds: nextDestIds },
    };
    setRows(updatedRows);
    setBuckets(newBuckets);
    onDataChange?.(updatedRows, newBuckets, bucketOrder);
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const isActiveBucket = bucketIds.includes(activeId);
    const isOverBucket = bucketIds.includes(overId);

    if (isActiveBucket && isOverBucket) {
      const oldIndex = bucketOrder.indexOf(activeId);
      const newIndex = bucketOrder.indexOf(overId);
      const updatedBucketOrder = arrayMove(bucketOrder, oldIndex, newIndex);
      
      setBucketOrder(updatedBucketOrder);
      onDataChange?.(rows, buckets, updatedBucketOrder);
      return;
    }

    const bucketId = findBucketId(buckets, activeId);
    if (!bucketId) return;

    if (bucketIds.includes(overId)) return;

    const bucket = buckets[bucketId];

    const oldIndex = bucket.rowIds.indexOf(activeId);
    const newIndex = bucket.rowIds.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedRowIds = arrayMove(bucket.rowIds, oldIndex, newIndex);
    const newBuckets = {
      ...buckets,
      [bucketId]: {
        ...bucket,
        rowIds: reorderedRowIds,
      },
    };
    
    const updatedRows = { ...rows };
    reorderedRowIds.forEach((rowId, index) => {
      updatedRows[rowId] = {
        ...updatedRows[rowId],
        display_index: index
      };
    });
    
    setRows(updatedRows);
    setBuckets(newBuckets);
    onDataChange?.(updatedRows, newBuckets, bucketOrder);
  }

  const handleBucketTitleChange = (bucketId: string, newTitle: string) => {
    const updatedBucket = { ...buckets[bucketId], title: newTitle };
    const updatedBuckets = { ...buckets, [bucketId]: updatedBucket };

    setBuckets(updatedBuckets);
    onDataChange?.(rows, updatedBuckets, bucketOrder);
  }

  const handleBucketFlagChange = (bucketId: string, isMandatory: boolean) => {
    const updatedBucket = { ...buckets[bucketId], is_mandatory: isMandatory };
    const updatedBuckets = { ...buckets, [bucketId]: updatedBucket };

    setBuckets(updatedBuckets);
    onDataChange?.(rows, updatedBuckets, bucketOrder);
  }

  const handleAddBucket = () => {
    const newId = uuidv4();
    const updated = { ...buckets, [newId]: { id: newId, title: "New Group", rowIds: [] } };
    const updatedOrder = [...bucketOrder, newId];
    setBuckets(updated);
    setBucketOrder(updatedOrder);
    onDataChange?.(rows, updated, updatedOrder);
  }

  const handleAddParameter = () => {
    onAddRow();
  }

  const handleDeleteRow = (rowId: string) => {
    const bucketId = findBucketId(buckets, rowId);
    if (!bucketId) return;

    const { [rowId]: deletedRow, ...remainingRows } = rows;
    const updatedBuckets = {
      ...buckets,
      [bucketId]: {
        ...buckets[bucketId],
        rowIds: buckets[bucketId].rowIds.filter(id => id !== rowId)
      }
    };

    setRows(remainingRows);
    setBuckets(updatedBuckets);
    onDataChange?.(remainingRows, updatedBuckets, bucketOrder);
    onDeleteRow(rowId);
  }

  const handleEditRow = (rowId: string, updatedRow: Row<T>) => {
    const updated = { ...rows, [rowId]: updatedRow };
    setRows(updated);
    onDataChange?.(updated, buckets, bucketOrder);
  }

  const handleDeleteBucket = (bucketId: string) => {
    setBucketToDelete(bucketId);
    setShowDeleteBucketModal(true);
  }

  const confirmDeleteBucket = () => {
    if (!bucketToDelete) return;
    
    const bucketId = bucketToDelete;
    const { [bucketId]: deletedBucket, ...rest } = buckets;
    const currentUngroupedLength = rest.ungrouped.rowIds.length;
    
    const updatedUngroupedRows = { ...rows };
    deletedBucket.rowIds.forEach((rowId, index) => {
      updatedUngroupedRows[rowId] = {
        ...updatedUngroupedRows[rowId],
        display_index: currentUngroupedLength + index
      };
    });
    
    const updatedBucketsOrder = bucketOrder.filter((id) => id !== bucketId);
    
    const updatedBuckets = {
      ...rest,
      ungrouped: {
        ...rest.ungrouped,
        rowIds: [...rest.ungrouped.rowIds, ...deletedBucket.rowIds],
      },
    };
    setRows(updatedUngroupedRows);
    setBuckets(updatedBuckets);
    setBucketOrder(updatedBucketsOrder);
    onDataChange?.(updatedUngroupedRows, updatedBuckets, updatedBucketsOrder);
    
    setShowDeleteBucketModal(false);
    setBucketToDelete(null);
  }

  return (
    <>
      <Modal
        onDismiss={() => setShowDeleteBucketModal(false)}
        visible={showDeleteBucketModal}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeleteBucketModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={confirmDeleteBucket}>Confirm</Button>
            </SpaceBetween>
          </Box>
        }
        header="Confirm Delete Group"
      >
        {bucketToDelete && `Are you sure you want to delete "${buckets[bucketToDelete]?.title}"? All related rows will be moved to the "Ungrouped" group`}
      </Modal>
      <div className="dnd-container">
        <div className="dnd-actions--container">
          <Button onClick={handleAddBucket} iconName='add-plus' variant="primary">Add Group</Button>
          <Button onClick={handleAddParameter} iconName='add-plus'>Add Parameter</Button>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="dnd-context">
            <BucketColumn 
              key="ungrouped"
              bucket={buckets.ungrouped} 
              rows={rows}
              onTitleChange={handleBucketTitleChange}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
              isEditable={false}
              renderItem={renderItem}
            />
            <SortableContext items={bucketIds} strategy={verticalListSortingStrategy}>
              {bucketIds.map((bucketId) => (
                <SortableBucket
                  key={bucketId}
                  bucket={buckets[bucketId]}
                  rows={rows}
                  onTitleChange={handleBucketTitleChange}
                  onFlagChange={handleBucketFlagChange}
                  onDeleteBucket={handleDeleteBucket}
                  onEditRow={handleEditRow}
                  onDeleteRow={handleDeleteRow}
                  renderItem={renderItem}
                />
              ))}
            </SortableContext>
          </div>
          <DragOverlay>
            {activeId && rows[activeId] ? (
              <div className="row-item" style={{ opacity: 0.8 }}>
                <button className="drag-handle"><Icon name="drag-indicator" /></button>
                {renderItem(rows[activeId], { onEdit: () => {}, onDelete: () => {}, canDelete: true }).content}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </>
  );
}

export default DraggableGroupedList;


